import type { ProductTerm } from '@sravni/react-ugc/dist/types/components/ProductTerms';
import { hasItem, normalizeTabs } from '@sravni/ugc/lib/specialOffers/helpers';
import {
  BUSINESS_OFFERS,
  BUSINESS_OFFERS_LIST,
  COMMON_OFFERS,
  EDUCATION_OFFERS,
  OTHER_OFFERS,
} from '@sravni/ugc/lib/specialOffers/lists';
import type { Tab, VITRINS_OFFERS_TYPE } from '@sravni/ugc/lib/specialOffers/types';

import type { Advertising } from '../interfaces/offers';
import type { IShortOrganization } from '../interfaces/organizations';
import { WIDGET_TYPE } from '../types/widgetType';

import { getTabsInfo } from './getTabsInfo';
import { getEducationLinks } from './offerLinks/getEducationLinks';
import { getLeasingLinks } from './offerLinks/getLeasingLinks';

// offer.organization._id -- займы
// offer.organization -- витрины, вклады и накопительные счета, курсы
export const getOrganizationIdFromOffer = (offer): string => (offer.organization._id || offer.organization).toString();

export const checkIsCard = ({ type }: Tab) => type === COMMON_OFFERS.creditCards || type === COMMON_OFFERS.debitCards;

type GetLogoParams = {
  item: any;
  offerType: Tab;
  organization?: IShortOrganization;
};

export const getLogo = ({ item, offerType, organization }: GetLogoParams) => {
  let logoSrc: string = null;

  const isCard = checkIsCard(offerType);

  const { productLogo } = item;

  if (isCard) {
    logoSrc = productLogo;
  } else if (offerType.type === COMMON_OFFERS.loan) {
    logoSrc = organization?.logotypes?.android || organization?.logo;
  } else {
    logoSrc = organization?.logotypes?.square || organization?.logo;
  }

  return logoSrc;
};

export const getAdvertising = (item: any): Advertising => item.ad || item.advertising;

export const getAdvertisingAction = ({
  tab,
  advertising,
}: {
  tab: Tab<VITRINS_OFFERS_TYPE>;
  advertising: Advertising;
}) => {
  if (tab.type === BUSINESS_OFFERS.biznesLeasing) {
    return 'Блок|Переход на витрину';
  }

  return advertising?.monetization?.kind === 'referral' ? 'Платный клик' : 'Переход к оформлению заявки';
};

export const getAdvertisingLabel = ({
  offerName,
  offerId,
  organizationName,
  isVerticalWidget,
  tab,
}: {
  offerName: string;
  offerId: string;
  organizationName: string;
  isVerticalWidget: boolean;
  tab: Tab<VITRINS_OFFERS_TYPE>;
}) => {
  if (isVerticalWidget && tab.type === OTHER_OFFERS.bukmekery) {
    return `${organizationName}|${offerName}|${offerId}`;
  }

  if (tab.type === BUSINESS_OFFERS.biznesLeasing) {
    const tabInfo = getTabsInfo(tab);
    return `${organizationName}|${offerName}|${tabInfo.generalLink}`;
  }

  return `${organizationName}|${offerName}`;
};

const OFFER_TYPES_WHOSE_DONT_LIKE_TO_SHOW_NO_LABEL = [
  BUSINESS_OFFERS.biznesLeasing,
  BUSINESS_OFFERS.biznesEkvajring,
  BUSINESS_OFFERS.biznesAccounting,
  BUSINESS_OFFERS.biznesRegistration,
  BUSINESS_OFFERS.biznesRko,
  BUSINESS_OFFERS.biznesKredity,
];

export const getConditions = (product: any, offerType: Tab, widgetType?: WIDGET_TYPE): ProductTerm[] => {
  const tabInfo = getTabsInfo(offerType);

  const conditionsFunction =
    widgetType === WIDGET_TYPE.vertical ? tabInfo.getVerticalWidgetConditions : tabInfo.getConditions;

  let initialConditions: ProductTerm[] = [];
  try {
    initialConditions = conditionsFunction?.(product) ?? [];
  } catch (e) {
    console.log({ e });
  }

  const labelKeys = initialConditions.map((condition) => condition.labelKey).filter(Boolean);

  const conditions = initialConditions
    .map((condition) => {
      const { labelKey, key, value } = condition;
      if (labelKeys.includes(key)) {
        return null;
      }

      if (hasItem(normalizeTabs(OFFER_TYPES_WHOSE_DONT_LIKE_TO_SHOW_NO_LABEL), tabInfo.key) && value === 'Нет') {
        return null;
      }

      if (labelKey) {
        const conditionAsLabel = initialConditions.find(({ key }) => key === labelKey);

        return {
          ...condition,
          ...(conditionAsLabel && { description: (conditionAsLabel.SOValue as string) ?? conditionAsLabel.value }),
        };
      }

      return condition;
    })
    .filter(Boolean);

  return conditions;
};

type GetClickAndDisplayLinksParams = {
  tabType: Tab;
  item: any;
  organization?: IShortOrganization;
  advertising?: Advertising;
};

export const getClickAndDisplayLinks = ({
  item,
  organization,
  tabType,
  advertising,
}: GetClickAndDisplayLinksParams): {
  clickLink?: string;
  displayLink?: string;
} => {
  let clickLink = '';
  let displayLink = '';

  if (tabType.type === EDUCATION_OFFERS.learning) {
    ({ clickLink, displayLink } = getEducationLinks({ item }));

    return { clickLink, displayLink };
  }

  if (tabType.type === BUSINESS_OFFERS.biznesLeasing) {
    ({ clickLink } = getLeasingLinks());

    return { clickLink, displayLink };
  }

  const hasAdvertising = advertising?.monetization?.pixels;

  if (hasAdvertising) {
    clickLink = advertising.monetization.pixels.click;
    displayLink = advertising.monetization.pixels.display;

    return { clickLink, displayLink };
  }

  const { url, alias, isCard } = item;
  const { linkToOneProduct, generalLink } = getTabsInfo(tabType);

  if (isCard) {
    clickLink = url;
  } else if (item.redirect) {
    clickLink = item.redirect;
  } else if (linkToOneProduct) {
    clickLink = `/bank/${organization.alias}/${linkToOneProduct}/${alias}/`;
  } else if (hasItem(BUSINESS_OFFERS_LIST, tabType)) {
    clickLink = generalLink;
  } else {
    clickLink = generalLink;
  }

  return { clickLink, displayLink };
};
